import { Box, Badge, Select, Text, useDisclosure, Flex, Tooltip, CircularProgress, CircularProgressLabel } from '@chakra-ui/react';
import React, { useState } from 'react';
import MyDiv from './employeeTable.style';
import { ChevronDownIcon } from '@chakra-ui/icons';
import AlertCard from "../../../../../components/AlertCard";
import { useNavigate } from "react-router-dom";
import * as routesNames from '../../../../../constants/routes';
import { CustomTable, CustomToast } from '../../../../../components';
import { useFetch } from '../../../../../hooks/useFetch';
import { Employee, EmployeeListProps, EmployeeModel, Header } from './model';
import { Column } from '../../../../../components/CustomTable/customTable';

const EmployeeTable: React.FC<EmployeeListProps> = ({ employees, statusDD, reload, setReload }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const [employeeStatuses, setEmployeeStatuses] = useState<Record<number, string>>(() => {
    const initialStatuses: Record<number, string> = {};
    employees.forEach(emp => {
      const match = statusDD.find(s => s.employeeStatus === emp.status);
      if (match) {
        initialStatuses[emp.id] = match.id.toString();

      }
    });
    return initialStatuses;
  });

  const [pendingStatus, setPendingStatus] = useState<string>('');
  const [pendingEmployeeId, setPendingEmployeeId] = useState<number | null>(null);
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);

  const handleStatusChange = (emp: EmployeeModel, newStatus: string) => {
    setPendingStatus(newStatus);
    setPendingEmployeeId(emp.id);
    onOpen();
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingEmployeeId || !pendingStatus) return;

    try {
      const payload = { employeeId: pendingEmployeeId, employeeStatusId: Number(pendingStatus), };
      const response = await fetchApi(`Hrlense_Employee/toggleEmpStatus/${pendingEmployeeId}`, "PUT", payload, "", "");

      if (response) {
        const updatedStatusName = statusDD.find(s => s.id.toString() === pendingStatus)?.employeeStatus;
        if (updatedStatusName) {
          setEmployeeStatuses(prev => ({ ...prev, [pendingEmployeeId]: pendingStatus }));
          setTimeout(() => {
            addToast({ message: "Status updated successfully.", status: "success" });
          }, 3000);
        }
        setReload && setReload(!reload);
      }
    } catch (error) {
      addToast({ message: "An error occurred while updating status.", status: "error" });
    }
    onClose();
  };

  const handleEmployeeDetail = (employee: Employee) => {
    const employeeId = employee.id;
    navigate(routesNames.EMPLOYEEDETAIL, { state: { employeeId } });
  };
const PROFILE_WEIGHTS = {
  personal: 25,
  employer: 25,
  family: 10,
  education: 15,
  bank: 10,
  documents: 15
};
  const getProfileColor = (percent: number) => {
  if (percent <= 40) return "red.400";
  if (percent <= 70) return "yellow.400";
  return "green.400";
};

const ProfileTooltip = ({ tabs }: { tabs: any }) => (
  <Box p={2}>
    {Object.entries(PROFILE_WEIGHTS).map(([key, max]) => (
      <Flex
        key={key}
        justify="space-between"
        align="center"
        gap={4}
        fontSize="12px"
        mb={1}
      >
        <Text textTransform="capitalize" color="gray.600">
          {key}
        </Text>
        <Text fontWeight="600">
          {tabs?.[key] ?? 0} / {max}
        </Text>
      </Flex>
    ))}
  </Box>
);

  return (
    <MyDiv>
      <Box className="table_wrapper" mt={2}>
        <CustomTable value={employees} >
          <Column header="S.No" body={(_: Employee, index: number) => <Text>{index + 1}</Text>} />

          <Column header="Employee ID" body={(emp: Employee) => (
            <Badge variant="subtle" className="empId font-poppins text_sm text_medium" onClick={() => handleEmployeeDetail(emp)}>
              {emp.empCode}
            </Badge>
          )}
          />

          <Column header="Name"
            body={(emp: Employee) => (
              <Flex align="center" gap={2}>
                <Box  w="8px" h="8px"  borderRadius="full" bg={emp.isOnline ? "green.500" : "red.400"}/>
                <Text>{emp.name}</Text>
              </Flex>
            )}
          />

          <Column field="email" header="Email" />
          <Column field="usCompany" header="US Company" body={(emp: Employee) => emp.usCompany || "Company Name"} />
          {/* <Column field="payrollCompany" header="Payroll Company" body={(emp: Employee) => emp.payrollCompany || "Company Name"} /> */}
          <Column field="branch" header="Branch" />
          <Column field="department" header="Department" />
          <Column field="designation" header="Designation" />
          <Column field="shift" header="Shift" />
          <Column
  header="Profile %"
  body={(emp: Employee) => {
    const percent = emp.profileCompletion?.totalPercentage ?? 0;

    return (
      <Tooltip
        hasArrow
        placement="left"
        bg="white"
        color="black"
        borderRadius="md"
        boxShadow="lg"
        label={<ProfileTooltip tabs={emp.profileCompletion?.tabs} />}
      >
        <Box cursor="pointer">
          <CircularProgress
            value={percent}
            size="40px"
            thickness="7px"
            color={getProfileColor(percent)}
            trackColor="gray.200"
          >
            <CircularProgressLabel
              fontSize="11px"
              fontWeight="600"
              color={getProfileColor(percent)}
            >
              {percent}%
            </CircularProgressLabel>
          </CircularProgress>
        </Box>
      </Tooltip>
    );
  }}
/>

          <Column header="Status" body={(emp: Employee) => {
            const selectedStatus = employeeStatuses[emp.id];
            const currentStatusObj = statusDD.find(s => s.id.toString() === selectedStatus);
            const isActive = currentStatusObj?.employeeStatus === "Active";
            return (
              <Text className="font-poppins text_medium text_xsm" px={2} py={1} borderRadius="4px" bg={isActive ? "#F0FFF4" : "#FFF5F5"} color={isActive ? "#38A169" : "#E53E3E"} textAlign="center">
                {currentStatusObj?.employeeStatus || "N/A"}
              </Text>
            );
          }}
          />
        </CustomTable>
      </Box>

      <AlertCard isOpen={isOpen} onClose={onClose} onConfirm={handleConfirmStatusChange} />
    </MyDiv>
  );
};

export default EmployeeTable;
